import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  CheckCircle, XCircle, Sparkle, ArrowLeft, DownloadSimple,
  ClockCounterClockwise, Trash, GitDiff, FileArrowUp, Note, ShieldCheck, Warning,
  PencilSimple,
} from "@phosphor-icons/react";

function StatusBadge({ status }) {
  const map = {
    approved: "text-emerald-700 bg-emerald-50 border-emerald-200",
    pending: "text-amber-700 bg-amber-50 border-amber-200",
    rejected: "text-red-700 bg-red-50 border-red-200",
  };
  return (
    <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border rounded-sm ${map[status]}`}>
      {status}
    </span>
  );
}

export default function DocumentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [cats, setCats] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [pdfFilename, setPdfFilename] = useState("document.pdf");
  const [comment, setComment] = useState("");
  const [summarizing, setSummarizing] = useState(false);
  const [acting, setActing] = useState(false);
  const [diffA, setDiffA] = useState("");
  const [diffB, setDiffB] = useState("");
  const [diffData, setDiffData] = useState(null);
  const [diffing, setDiffing] = useState(false);
  const [newRevFile, setNewRevFile] = useState(null);
  const [newRevNotes, setNewRevNotes] = useState("");
  const [uploadingRev, setUploadingRev] = useState(false);
  const [benchmark, setBenchmark] = useState(null);
  const [benchmarking, setBenchmarking] = useState(false);

  const load = useCallback(async () => {
    const [d, c] = await Promise.all([
      api.get(`/documents/${id}`),
      api.get("/categories"),
    ]);
    setDoc(d.data);
    setCats(c.data);
    setBenchmark(d.data?.industry_benchmark || null);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!doc) return;
    const token = localStorage.getItem("dms_token") || "";
    const cur = doc.versions.find((v) => v.version === doc.current_version);
    const fname = cur?.original_filename || "";
    const ext = fname.includes(".") ? fname.split(".").pop().toLowerCase() : "";
    const ct = cur?.content_type || "";
    const officeExts = ["doc","docx","odt","rtf","xls","xlsx","ods","ppt","pptx","odp"];
    const needsPreviewRoute =
      ct === "application/pdf" || ext === "pdf" ||
      officeExts.includes(ext) ||
      ct.includes("wordprocessingml") || ct.includes("spreadsheetml") || ct.includes("presentationml");
    const path = needsPreviewRoute ? "preview" : "download";
    const url = `${API}/documents/${doc.id}/${path}?auth=${encodeURIComponent(token)}`;
    setPreviewUrl(url);
    // Download URL: portal ALWAYS serves PDF (attachment). Filename hint = doc TITLE (matches library).
    setDownloadUrl(`${API}/documents/${doc.id}/download?auth=${encodeURIComponent(token)}`);
    const rawTitle = (doc.title || "document").trim();
    const stripped = rawTitle.replace(/\.(pdf|docx?|odt|rtf|xls[xm]?|ppt[xm]?|html?)$/i, "");
    setPdfFilename(`${stripped}.pdf`);
    // Default diff: latest vs previous, if 2+ versions
    if (doc.versions?.length >= 2 && !diffA && !diffB) {
      setDiffA(String(doc.current_version - 1));
      setDiffB(String(doc.current_version));
    }
  }, [doc, diffA, diffB]);

  const [refreshingPreview, setRefreshingPreview] = useState(false);

  const forceRefreshPreview = async () => {
    if (!doc) return;
    setRefreshingPreview(true);
    try {
      const token = localStorage.getItem("dms_token") || "";
      const bust = Date.now();
      // Fire fresh conversion server-side (skips cached tofu-PDF, overwrites cache)
      await fetch(`${API}/documents/${doc.id}/preview?fresh=1&auth=${encodeURIComponent(token)}&_=${bust}`, { cache: "no-store" });
      const url = `${API}/documents/${doc.id}/preview?auth=${encodeURIComponent(token)}&_=${bust}`;
      setPreviewUrl(url);
      toast.success("Preview regenerated");
    } catch (e) {
      toast.error("Refresh failed — try again");
    } finally {
      setRefreshingPreview(false);
    }
  };

  const versionOptions = useMemo(
    () => (doc?.versions || []).map((v) => v.version).sort((a, b) => a - b),
    [doc]
  );

  const runDiff = useCallback(async (aVer, bVer) => {
    const A = aVer ?? Number(diffA);
    const B = bVer ?? Number(diffB);
    if (!A || !B || A === B) {
      return toast.error("Pick two different versions to compare");
    }
    setDiffing(true);
    try {
      const { data } = await api.get(`/documents/${doc.id}/diff`, {
        params: { a: A, b: B },
      });
      setDiffData(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setDiffing(false);
    }
  }, [diffA, diffB, doc]);

  // Auto-run diff for reviewers on a pending revision (v ≥ 2)
  useEffect(() => {
    if (!doc || diffData || diffing) return;
    const isReviewer = ["admin", "manager"].includes(user?.role);
    if (isReviewer && doc.status === "pending" && doc.versions?.length >= 2) {
      runDiff(doc.current_version - 1, doc.current_version);
    }
  }, [doc, user, diffData, diffing, runDiff]);

  if (!doc) return <div className="text-zinc-400 text-sm">Loading…</div>;

  const canApprove = ["admin", "manager"].includes(user?.role) && doc.status === "pending";
  const catName = cats.find((c) => c.id === doc.category_id)?.name || "—";
  const currentVersion = doc.versions.find((v) => v.version === doc.current_version);
  const filename = currentVersion?.original_filename || "";
  const ext = filename.includes(".") ? filename.split(".").pop().toLowerCase() : "";
  const ctype = currentVersion?.content_type || "";
  const isPdf = ctype === "application/pdf" || ext === "pdf";
  const isImage = ctype.startsWith("image/") || ["png","jpg","jpeg","webp","gif"].includes(ext);
  const isOfficeText = ["docx","xlsx","pptx","doc","xls","ppt","txt","md","csv","log"].includes(ext)
    || ctype.startsWith("text/")
    || ctype.includes("wordprocessingml")
    || ctype.includes("spreadsheetml")
    || ctype.includes("presentationml");
  const previewText = currentVersion?.extracted_text || "";

  const act = async (action) => {
    setActing(true);
    try {
      await api.post(`/documents/${doc.id}/approve`, { action, comment });
      toast.success(action === "approve" ? "Approved" : "Rejected");
      setComment("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setActing(false);
    }
  };

  const summarize = async () => {
    setSummarizing(true);
    try {
      const { data } = await api.post(`/documents/${doc.id}/summarize`);
      setDoc((d) => ({ ...d, ai_summary: data.summary }));
      toast.success("AI summary generated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSummarizing(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Deleted");
      nav("/documents");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const runBenchmark = async () => {
    setBenchmarking(true);
    try {
      const { data } = await api.post(`/documents/${doc.id}/industry-benchmark`);
      setBenchmark(data);
      toast.success("Industry benchmark generated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBenchmarking(false);
    }
  };

  const uploadRevision = async (e) => {
    e.preventDefault();
    if (!newRevFile) return toast.error("Pick a file for the new revision");
    setUploadingRev(true);
    try {
      const fd = new FormData();
      fd.append("file", newRevFile);
      fd.append("revision_notes", newRevNotes);
      const { data } = await api.post(`/documents/${doc.id}/version`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded v${data.version} — awaiting approval`);
      setNewRevFile(null);
      setNewRevNotes("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploadingRev(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="document-detail">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/documents" className="label-caps hover:text-navy transition-colors">
            <ArrowLeft size={12} /> Back to library
          </Link>
          <h2 className="font-display text-3xl text-navy uppercase mt-2">{doc.title}</h2>
          <div className="mt-1 flex items-center gap-3 text-xs text-zinc-500">
            <StatusBadge status={doc.status} />
            <span className="font-mono">v{doc.current_version}</span>
            <span>{catName}</span>
            <span>·</span>
            <span>Uploaded by {doc.created_by_name}</span>
          </div>
        </div>
        <div className="flex gap-2">
          {(user?.role === "admin" || user?.role === "manager" || doc.status === "approved") && (
            <Link
              to={`/documents/${doc.id}/edit`}
              data-testid="edit-doc-button"
              className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-navy rounded-sm bg-white hover:bg-navy hover:text-white text-navy transition-colors"
            >
              <PencilSimple size={14} /> Edit inline
            </Link>
          )}
          <a
            href={downloadUrl}
            download={pdfFilename}
            data-testid="download-button"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-zinc-300 rounded-sm bg-white hover:bg-zinc-100"
          >
            <DownloadSimple size={14} /> Download PDF
          </a>
          {["admin", "manager"].includes(user?.role) && (
            <Button variant="outline" data-testid="delete-doc" onClick={del} className="rounded-sm">
              <Trash size={14} className="mr-1" /> Delete
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 space-y-6">
          <div className="card-flat overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
              <div className="label-caps">Preview</div>
              <div className="flex items-center gap-3">
                {user?.role === "admin" && (isPdf || isOfficeText) && (
                  <button
                    onClick={forceRefreshPreview}
                    disabled={refreshingPreview}
                    data-testid="refresh-preview-btn"
                    title="Regenerate the preview PDF from source (bypass cache)"
                    className="text-[10px] font-mono uppercase tracking-widest text-navy hover:text-cyan disabled:opacity-40 border border-zinc-200 hover:border-cyan px-2 py-1 rounded-sm"
                  >
                    {refreshingPreview ? "Refreshing…" : "↻ Refresh"}
                  </button>
                )}
                <div className="text-xs font-mono text-zinc-500">{currentVersion?.original_filename}</div>
              </div>
            </div>
            <div className="bg-zinc-100 min-h-[520px] flex items-center justify-center">
              {(isPdf || isOfficeText) && (
                <iframe
                  title="preview"
                  src={previewUrl}
                  className="w-full h-[640px] bg-white"
                  data-testid="pdf-preview"
                />
              )}
              {!isPdf && !isOfficeText && isImage && (
                <img src={previewUrl} alt="preview" className="max-h-[640px] object-contain" data-testid="image-preview" />
              )}
              {!isPdf && !isImage && !isOfficeText && (
                <div className="text-center p-10">
                  <img
                    src="https://images.unsplash.com/photo-1721244654394-36a7bc2da288?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxibHVlcHJpbnQlMjBkb2N1bWVudHMlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg1NzQ1MzUwfDA&ixlib=rb-4.1.0&q=85"
                    alt=""
                    className="w-40 h-40 object-cover opacity-30 mx-auto grayscale"
                  />
                  <div className="mt-4 text-sm text-zinc-500">Preview not available for this file type. Use Download.</div>
                </div>
              )}
            </div>
          </div>

          <div className="card-flat">
            <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
              <div>
                <div className="label-caps"><GitDiff size={12} /> Version compare</div>
                <div className="font-display text-lg text-navy uppercase mt-1">Revision diff</div>
              </div>
              {versionOptions.length >= 2 && (
                <div className="flex items-center gap-2">
                  <Select value={diffA} onValueChange={setDiffA}>
                    <SelectTrigger className="w-24 h-9" data-testid="diff-version-a">
                      <SelectValue placeholder="A" />
                    </SelectTrigger>
                    <SelectContent>
                      {versionOptions.map((v) => (
                        <SelectItem key={`a-${v}`} value={String(v)}>v{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-slate-400 text-sm">→</span>
                  <Select value={diffB} onValueChange={setDiffB}>
                    <SelectTrigger className="w-24 h-9" data-testid="diff-version-b">
                      <SelectValue placeholder="B" />
                    </SelectTrigger>
                    <SelectContent>
                      {versionOptions.map((v) => (
                        <SelectItem key={`b-${v}`} value={String(v)}>v{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    onClick={() => runDiff()}
                    disabled={diffing}
                    data-testid="run-diff"
                    className="btn-navy"
                  >
                    {diffing ? "Comparing…" : "Compare →"}
                  </button>
                </div>
              )}
            </div>
            <div className="p-5">
              {versionOptions.length < 2 && (
                <div className="text-sm text-slate-500">
                  Upload a second version of this document to see a side-by-side diff.
                </div>
              )}
              {versionOptions.length >= 2 && !diffData && !diffing && (
                <div className="text-sm text-slate-500">
                  Pick two versions and click <strong>Compare</strong> to see line-by-line changes in extracted text.
                </div>
              )}
              {diffing && <Skeleton className="h-40 w-full" />}
              {diffData && !diffing && (
                <div>
                  <div className="flex items-center gap-4 mb-3 text-xs font-mono">
                    <span className="text-emerald-700">+{diffData.added} added</span>
                    <span className="text-red-700">−{diffData.removed} removed</span>
                    <span className="text-slate-500">
                      v{diffData.a} ({diffData.a_meta?.uploaded_by}) → v{diffData.b} ({diffData.b_meta?.uploaded_by})
                    </span>
                  </div>
                  <pre
                    data-testid="diff-output"
                    className="font-mono text-xs leading-5 bg-slate-950 text-slate-200 p-4 rounded-sm overflow-auto max-h-96"
                  >
                    {diffData.unified.split("\n").map((line, i) => {
                      let cls = "text-slate-300";
                      if (line.startsWith("+++") || line.startsWith("---")) cls = "text-cyan";
                      else if (line.startsWith("@@")) cls = "text-amber-400";
                      else if (line.startsWith("+")) cls = "text-emerald-400";
                      else if (line.startsWith("-")) cls = "text-red-400";
                      return <div key={i} className={cls}>{line || " "}</div>;
                    })}
                  </pre>
                </div>
              )}
            </div>
          </div>

          <div className="card-flat">
            <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
              <div>
                <div className="label-caps flex items-center gap-2"><Sparkle size={12} /> AI summary</div>
                <div className="font-display text-lg text-navy uppercase mt-1">Auto-generated overview</div>
              </div>
              <button
                onClick={summarize}
                disabled={summarizing}
                data-testid="generate-summary"
                className="btn-navy"
              >
                {summarizing ? "Generating…" : doc.ai_summary ? "Regenerate" : "Generate →"}
              </button>
            </div>
            <div className="p-5">
              {summarizing && (
                <div className="space-y-3">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-5/6" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              )}
              {!summarizing && doc.ai_summary && (
                <div className="prose prose-sm max-w-none whitespace-pre-wrap text-zinc-800" data-testid="ai-summary-text">
                  {doc.ai_summary}
                </div>
              )}
              {!summarizing && !doc.ai_summary && (
                <div className="text-sm text-zinc-500">
                  Click &quot;Generate summary&quot; to produce a structured overview using Gemini 2.5 Flash. Works with PDFs and text files.
                </div>
              )}
            </div>
          </div>

          {["admin", "manager"].includes(user?.role) && (
            <div className="card-flat" data-testid="benchmark-card">
              <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
                <div>
                  <div className="label-caps flex items-center gap-2"><ShieldCheck size={12} weight="fill" className="text-cyan" /> Compliance benchmark</div>
                  <div className="font-display text-lg text-navy uppercase mt-1">Industry-standard reference</div>
                  {benchmark?.standards?.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {benchmark.standards.map((s) => (
                        <span
                          key={s}
                          className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 bg-cyan/10 border border-cyan/40 rounded-sm text-navy"
                        >
                          <ShieldCheck size={10} weight="fill" className="text-cyan" />
                          {s}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={runBenchmark}
                  disabled={benchmarking}
                  data-testid="generate-benchmark"
                  className="btn-cyan"
                >
                  {benchmarking ? "Analyzing…" : benchmark?.reference_text ? "Regenerate" : "Generate →"}
                </button>
              </div>
              <div className="p-5">
                {benchmarking && (
                  <div className="space-y-3">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-40 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                )}
                {!benchmarking && !benchmark?.reference_text && (
                  <div className="text-sm text-zinc-500">
                    Generate an AI-produced reference version of this document
                    that satisfies the industry standards configured on its
                    department. You&apos;ll see a side-by-side comparison plus a
                    prioritized list of compliance gaps in the current upload.
                  </div>
                )}
                {!benchmarking && benchmark?.reference_text && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="label-caps text-slate-600 mb-1.5">
                          Your document · v{doc.current_version}
                        </div>
                        <pre
                          data-testid="benchmark-your-doc"
                          className="text-xs leading-5 bg-slate-50 border border-slate-200 p-3 rounded-sm overflow-auto max-h-96 whitespace-pre-wrap font-sans text-zinc-800"
                        >
                          {(currentVersion?.extracted_text || "").slice(0, 6000) || "(No extracted text available.)"}
                        </pre>
                      </div>
                      <div>
                        <div className="label-caps text-cyan mb-1.5 flex items-center gap-1">
                          <ShieldCheck size={12} weight="fill" /> Industry standard reference
                        </div>
                        <pre
                          data-testid="benchmark-reference"
                          className="text-xs leading-5 bg-cyan/5 border border-cyan/30 p-3 rounded-sm overflow-auto max-h-96 whitespace-pre-wrap font-sans text-zinc-800"
                        >
                          {benchmark.reference_text}
                        </pre>
                      </div>
                    </div>

                    {benchmark.gaps?.length > 0 && (
                      <div className="mt-5" data-testid="benchmark-gaps">
                        <div className="label-caps text-amber-700 mb-2 flex items-center gap-1">
                          <Warning size={12} weight="fill" /> Compliance gaps ({benchmark.gaps.length})
                        </div>
                        <ul className="space-y-2">
                          {benchmark.gaps.map((g, i) => {
                            const sev = (g.severity || "medium").toLowerCase();
                            const sevCls =
                              sev === "high"
                                ? "border-red-300 bg-red-50 text-red-800"
                                : sev === "low"
                                ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                                : "border-amber-300 bg-amber-50 text-amber-800";
                            return (
                              <li
                                key={i}
                                data-testid={`benchmark-gap-${i}`}
                                className={`border-l-4 rounded-sm p-3 text-sm ${sevCls}`}
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-[10px] font-mono font-semibold uppercase tracking-wider">
                                    {sev}
                                  </span>
                                  {g.clause && (
                                    <span className="text-[10px] font-mono px-1.5 py-0.5 bg-white border border-current/30 rounded-sm">
                                      {g.clause}
                                    </span>
                                  )}
                                </div>
                                <div className="font-medium">{g.issue}</div>
                                {g.fix && (
                                  <div className="text-xs mt-1 opacity-80">
                                    <span className="font-semibold">Fix:</span> {g.fix}
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    <div className="mt-4 text-[10px] font-mono text-slate-500">
                      Generated {new Date(benchmark.generated_at).toLocaleString()} · by {benchmark.generated_by} · for v{benchmark.for_version}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="card-flat p-5">
            <div className="label-caps mb-3">Metadata</div>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-zinc-500">Category</dt><dd className="text-zinc-900">{catName}</dd></div>
              <div className="flex justify-between"><dt className="text-zinc-500">Expiry</dt><dd className="font-mono text-zinc-900">{doc.expiry_date ? new Date(doc.expiry_date).toLocaleDateString() : "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-zinc-500">Created</dt><dd className="font-mono text-zinc-900">{new Date(doc.created_at).toLocaleDateString()}</dd></div>
              <div className="flex justify-between"><dt className="text-zinc-500">Size</dt><dd className="font-mono text-zinc-900">{(currentVersion?.size / 1024).toFixed(1)} KB</dd></div>
            </dl>
            {doc.tags?.length > 0 && (
              <div className="mt-4">
                <div className="label-caps mb-2">Tags</div>
                <div className="flex flex-wrap gap-1">
                  {doc.tags.map((t) => (
                    <span key={t} className="text-xs px-2 py-0.5 bg-zinc-100 border border-zinc-200 rounded-sm font-mono">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {doc.description && (
              <div className="mt-4">
                <div className="label-caps mb-1">Description</div>
                <p className="text-sm text-zinc-700">{doc.description}</p>
              </div>
            )}
          </div>

          {canApprove && (
            <div className="card-flat p-5">
              <div className="label-caps mb-3 text-blue-600">Approval workflow</div>
              {currentVersion?.revision_notes && (
                <div
                  data-testid="review-revision-notes"
                  className="mb-3 rounded-sm border border-cyan/30 bg-cyan/5 p-3"
                >
                  <div className="label-caps text-cyan mb-1 flex items-center gap-1.5">
                    <Note size={12} weight="fill" /> Uploader&apos;s notes · v{doc.current_version}
                  </div>
                  <p className="text-sm text-zinc-800 whitespace-pre-wrap italic">
                    &ldquo;{currentVersion.revision_notes}&rdquo;
                  </p>
                  <div className="text-[10px] font-mono text-zinc-500 mt-1.5">
                    — {currentVersion.uploaded_by_name}
                  </div>
                </div>
              )}
              {doc.versions?.length >= 2 && (
                <div className="mb-3 text-xs text-zinc-600 flex items-center gap-1.5">
                  <GitDiff size={12} className="text-navy" />
                  Comparing <span className="font-mono text-navy">v{doc.current_version - 1} → v{doc.current_version}</span>.
                  <span className="text-zinc-400">Diff below.</span>
                </div>
              )}
              <Textarea
                data-testid="approval-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Comment (optional)"
                rows={3}
              />
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Button
                  disabled={acting}
                  onClick={() => act("approve")}
                  data-testid="approve-button"
                  className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <CheckCircle size={16} className="mr-1" /> Approve
                </Button>
                <Button
                  disabled={acting}
                  onClick={() => act("reject")}
                  data-testid="reject-button"
                  variant="outline"
                  className="rounded-sm border-red-300 text-red-700 hover:bg-red-50"
                >
                  <XCircle size={16} className="mr-1" /> Reject
                </Button>
              </div>
            </div>
          )}

          <div className="card-flat p-5" data-testid="revision-history">
            <div className="label-caps mb-3 flex items-center gap-2">
              <ClockCounterClockwise size={12} /> Revision history
            </div>
            <ul className="space-y-3 text-sm">
              {[...doc.versions].reverse().map((v) => (
                <li key={v.version} className={`border-l-2 pl-3 ${v.version === doc.current_version ? "border-cyan" : "border-zinc-200"}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-semibold text-navy">v{v.version}</span>
                    <span className="text-zinc-500 text-xs font-mono">
                      {new Date(v.revised_at || v.uploaded_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">by {v.uploaded_by_name || "—"}</div>
                  {v.revision_notes && (
                    <div data-testid={`revision-notes-v${v.version}`} className="text-xs text-zinc-700 mt-1 pl-2 border-l-2 border-cyan/40 italic">
                      {v.revision_notes}
                    </div>
                  )}
                </li>
              ))}
            </ul>

            <form onSubmit={uploadRevision} className="mt-4 pt-4 border-t border-slate-200 space-y-2" data-testid="upload-revision-form">
              <div className="label-caps flex items-center gap-2"><FileArrowUp size={12} /> Upload new revision</div>
              <label className="block">
                <input
                  type="file"
                  className="hidden"
                  data-testid="revision-file-input"
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.dwg,.dxf,.docx,.xlsx,.pptx"
                  onChange={(e) => setNewRevFile(e.target.files?.[0] || null)}
                />
                <span className="cursor-pointer inline-flex items-center gap-2 w-full px-3 py-2 text-xs border border-slate-300 rounded-sm bg-white hover:bg-slate-50 transition-colors">
                  <FileArrowUp size={12} />
                  <span className="truncate">{newRevFile ? newRevFile.name : "Choose file…"}</span>
                </span>
              </label>
              <Textarea
                data-testid="revision-notes-input"
                value={newRevNotes}
                onChange={(e) => setNewRevNotes(e.target.value)}
                placeholder="What changed in this revision? (optional)"
                rows={2}
                className="text-sm"
              />
              <Button
                type="submit"
                disabled={uploadingRev || !newRevFile}
                data-testid="submit-revision"
                className="w-full rounded-sm bg-navy hover:bg-navy/90 text-white"
              >
                <Note size={14} className="mr-1.5" />
                {uploadingRev ? "Uploading…" : `Upload as v${doc.current_version + 1}`}
              </Button>
            </form>
          </div>

          {doc.approvals?.length > 0 && (
            <div className="card-flat p-5">
              <div className="label-caps mb-3">Approval log</div>
              <ul className="space-y-3 text-sm">
                {doc.approvals.map((a, i) => (
                  <li key={i} className={`border-l-2 pl-3 ${a.action === "approve" ? "border-emerald-500" : "border-red-500"}`}>
                    <div className="text-zinc-900 font-medium capitalize">{a.action}d</div>
                    <div className="text-xs text-zinc-500">by {a.by_user_name} · v{a.version} · {new Date(a.at).toLocaleDateString()}</div>
                    {a.comment && <div className="text-sm text-zinc-700 mt-1">&quot;{a.comment}&quot;</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
